import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Tag} from "primereact/tag";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {
    NOISE_DB_IMPACT_SURROUNDING,
    NOISE_DB_NO_IMPACT,
    noiseLevelLabel,
} from "../../api/payload-types/locationinfo-noise";
import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {useMapSettings} from "../../hooks/useMapSettings";
import {selectAddressData} from "../../redux/addressSlice";
import {
    selectEmissionData,
    selectEmissionError,
    selectEmissionLoading,
    selectNoiseData,
    selectNoiseError,
    selectNoiseLoading,
} from "../../redux/locationInfoSlice";
import {LocationInfoEndpoint, queryLocationInfoAtCoordinateAsync} from "../../redux/locationInfoThunk";
import {LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND, selectCapabilities} from "../../redux/wmtsSlice";
import {LayerFactory, resolutionFromScale} from "../../util/maps";
import {tooltipHandler} from "../../util/mapTooltipUtil";
import {
    PERMISSION_RULE_EMISSION_TAB,
    PERMISSION_RULE_LOCATION_EMISSION,
    PERMISSION_RULE_LOCATION_NOISE,
} from "../../util/permissions";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

const NOISE_COLORS = ["#FEDD79", "#FEB751", "#FD8D3C", "#F45629", "#DF2722"];

/** Fallback tag style for the "no impact"/"no color class" case in the Belastung columns. */
const FALLBACK_TAG_STYLE = {backgroundColor: "#E5E7EB", color: "rgb(75, 85, 99)"};

const NOISE_CLASSES = {
    55: {dbLow: 55, dbHigh: 59, color: NOISE_COLORS[0]},
    60: {dbLow: 60, dbHigh: 64, color: NOISE_COLORS[1]},
    65: {dbLow: 65, dbHigh: 69, color: NOISE_COLORS[2]},
    70: {dbLow: 70, dbHigh: 74, color: NOISE_COLORS[3]},
    75: {dbLow: 75, dbHigh: 100, color: NOISE_COLORS[4]},
};
export const NOISE_SOURCES = {
    Bahnschiene: {mainSourceKey: "main_source_rail", layer: "reva:laermkarte_schienen"},
    Industrie: {mainSourceKey: "main_source_industry", layer: "reva:laermkarte_industrie"},
    Autobahn: {mainSourceKey: "main_source_street", layer: "reva:laermkarte_strassen"},
    Flughafen: {mainSourceKey: "main_source_airport", layer: "reva:laerm_flug_gs_default"},
    Geschäft: {mainSourceKey: "main_source_pois", layer: "reva:laermkarte_pois"},
    Party: {mainSourceKey: "main_source_pois", layer: "reva:laermkarte_pois"},
    Freizeit: {mainSourceKey: "main_source_pois", layer: "reva:laermkarte_pois"},
    Sport: {mainSourceKey: "main_source_pois", layer: "reva:laermkarte_pois"},
    Hauptstrasse: {mainSourceKey: "main_source_street", layer: "reva:laermkarte_strassen"},
    Nebenstrasse: {mainSourceKey: "main_source_street", layer: "reva:laermkarte_strassen"},
    "S-Bahnschiene": {mainSourceKey: "main_source_rail", layer: "reva:laermkarte_schienen"},
    "Str-Bahnschiene": {mainSourceKey: "main_source_rail", layer: "reva:laermkarte_schienen"},
    "U-Bahnschiene Oberirdisch": {mainSourceKey: "main_source_rail", layer: "reva:laermkarte_schienen"},
};

/**
 * `kategorie` ist der Titel aus basisdaten (z. B. „Hauptstrasse“). Die Schlüssel von NOISE_SOURCES und
 * `noise.sub_category` stehen in ss-Schreibweise, damit Titel mit und ohne ß gleich zugeordnet werden (L-B8, #19302).
 */
const noiseCategoryKey = (kategorie) => kategorie?.replace(/ß/g, "ss");

/** Hauptkategorie und Layer einer Lärm-Unterkategorie, undefined für unbekannte Titel. */
export function noiseSourceOf(kategorie) {
    return NOISE_SOURCES[noiseCategoryKey(kategorie)];
}

/** Anzeigename einer Lärm-Unterkategorie; Titel ohne Eintrag unter `noise.sub_category` bleiben wie geliefert. */
export function noiseSubCategoryLabel(kategorie, t) {
    return kategorie ? t(`noise.sub_category.${noiseCategoryKey(kategorie)}`, {defaultValue: kategorie}) : kategorie;
}

const NOISE_LAYERS = [
    {key: "noise_street", name: "reva:laermkarte_strassen", title: "Straßenlärm"},
    {key: "noise_rails", name: "reva:laermkarte_schienen", title: "Schienenlärm"},
    {key: "noise_pois", name: "reva:laermkarte_pois", title: "Sonstige Lärmquellen"},
    {key: "noise_industry", name: "reva:laerm_industrie_gs_default", title: "Industrielärm"},
    {key: "noise_airport", name: "reva:laerm_flug_gs_default", title: "Fluglärm"},
];

/**
 * The single parameterized GeoServer layer that renders all non-noise emission types.
 * The emission type is selected via a `viewparams=id:<id>` WMS parameter.
 */
const EMISSION_LAYER_NAME = "reva:emissionskarte";

/**
 * Selectable emission types. `id` is the GeoServer `viewparams` id used on {@link EMISSION_LAYER_NAME},
 * `key` is the i18n key (`emission.types.<key>`) and `kategorie` links a type to its entry in the
 * emission LocationInfo response (`null` for noise, which uses the dedicated noise layers/endpoint).
 * @typedef {{id:number,key:string,kategorie:(string|null)}} EmissionType
 * @type {EmissionType[]}
 */
const EMISSION_TYPES = [
    {id: 1, key: "laerm", kategorie: null},
    {id: 41, key: "abfall", kategorie: "Abfall"},
    {id: 39, key: "funkmast", kategorie: "Radiomast"},
    {id: 36, key: "industrie", kategorie: "Industrie"},
    {id: 37, key: "landwirtschaft", kategorie: "Landwirtschaft"},
    {id: 38, key: "strommast", kategorie: "Elektrizitätsmast"},
    {id: 40, key: "windkraft", kategorie: "Windturbine"},
];

const NOISE_EMISSION_TYPE = EMISSION_TYPES[0];
/** Nicht-Lärm-Emissionstypen (die über den parametrisierten Emissionslayer gerendert werden). */
const SELECTABLE_EMISSION_TYPES = EMISSION_TYPES.filter((et) => et.id !== NOISE_EMISSION_TYPE.id);

// Sichtbare Layer stecken in EINEM Set aus Tokens. Lärm-Token = GeoServer-Layername,
// Emissions-Token = "emission:<id>". Lärm- und Emissionslayer schließen sich gegenseitig aus:
// additiv nur innerhalb der jeweiligen Familie, nie gemischt.
const emissionToken = (id) => `emission:${id}`;
const isEmissionToken = (tok) => typeof tok === "string" && tok.startsWith("emission:");
const emissionIdFromToken = (tok) => Number(tok.slice("emission:".length));

function NoiseLegend() {
    const {t} = useTranslation();
    return (
        <div className="OpenLayersLegendPanel">
            <p className="noise-location-classes">
                <span className="noise-location-class noise-location-class-5"></span>
                {t("noise.class_very_high", {
                    low: 75,
                    high: 100,
                })}
                <br />
                <span className="noise-location-class noise-location-class-4"></span>
                {t("noise.class_high", {
                    low: 70,
                    high: 74,
                })}
                <br />
                <span className="noise-location-class noise-location-class-3"></span>
                {t("noise.class_elevated", {
                    low: 65,
                    high: 69,
                })}
                <br />
                <span className="noise-location-class noise-location-class-2"></span>
                {t("noise.class_moderate", {
                    low: 60,
                    high: 64,
                })}
                <br />
                <span className="noise-location-class noise-location-class-1"></span>
                {t("noise.class_low", {
                    low: 55,
                    high: 59,
                })}
            </p>
        </div>
    );
}

function NoiseNotes() {
    const {t} = useTranslation();
    return (
        <div className="ml-2 pr-2">
            <h3 className="mt-3 mb-2">{t("noise.explanation_heading")}</h3>
            <p>
                {t("noise.explanation_p1a")}
                <sub>DEN</sub>
                {t("noise.explanation_p1b")}
            </p>
            <p className="mb-0">{t("noise.explanation_p2")}</p>
        </div>
    );
}

function EmissionNotes() {
    const {t} = useTranslation();
    return (
        <div className="ml-2 pr-2">
            <h3 className="mt-3 mb-2">{t("emission.explanation_heading")}</h3>
            <p className="mb-0">{t("emission.explanation")}</p>
        </div>
    );
}

/** Jede Hauptkategorie (mainSourceKey) genau einmal, mit ihrem (allen Unterkategorien gemeinsamen) Layer. */
const NOISE_MAIN_SOURCES = [...new Map(Object.values(NOISE_SOURCES).map((s) => [s.mainSourceKey, s])).values()];

export function withAllKnownNoiseCategories(noise) {
    const impacted = noise.filter((n) => n.dbLow != null || n.dB === NOISE_DB_IMPACT_SURROUNDING);
    const presentMainSourceKeys = new Set(
        impacted.map((n) => noiseSourceOf(n.kategorie)?.mainSourceKey).filter(Boolean),
    );
    const missingMainSources = NOISE_MAIN_SOURCES.filter(
        ({mainSourceKey}) => !presentMainSourceKeys.has(mainSourceKey),
    ).map(({mainSourceKey, layer}) => ({
        kategorie: "",
        mainSourceKey,
        layer,
        dB: NOISE_DB_NO_IMPACT,
        dbLow: null,
        dbHigh: null,
        name: null,
        distanceM: null,
        coord: null,
    }));
    return [...impacted, ...missingMainSources];
}

/**
 * Table of the noise categories affecting the address: one row per belasteter Unterkategorie (auch
 * "Belastung in Umgebung"), plus
 * one collapsed "no impact" row (leere Unterkategorie) per Hauptkategorie ohne jede Belastung.
 * Rows are pre-sorted by noise level (Belastung) high→low. Clicking a row toggles that source's
 * noise layer on the map (mehrere gleichzeitig); the rows of every currently shown noise layer
 * are highlighted.
 * @param {{noiseData:Array, visibleLayers:Set<string>, onToggleLayer:(layerName:string)=>void}} props
 */
function NoiseDetails({noiseData, visibleLayers, onToggleLayer}) {
    const {t} = useTranslation();
    if (!noiseData) {
        return <LoadingIndicator loading={!noiseData} />;
    }
    const rows = withAllKnownNoiseCategories(noiseData)
        .map((n) => {
            const {kategorie, dB, dbLow, mainSourceKey, layer} = n;
            const dbLabel = noiseLevelLabel(dB, t);
            const source = noiseSourceOf(kategorie) ?? (mainSourceKey ? {mainSourceKey, layer} : undefined);
            const mainSource = source ? t(`noise.${source.mainSourceKey}`) : "";
            const layerName = source?.layer;
            return {
                kategorie: noiseSubCategoryLabel(kategorie, t),
                mainSource,
                dB: dbLabel,
                layerName,
                rowKey: kategorie || mainSourceKey,
                ...NOISE_CLASSES?.[dbLow],
            };
        })
        .sort((a, b) => (b.dbLow ?? -1) - (a.dbLow ?? -1));
    // Klick auf eine Zeile schaltet den Layer der Hauptkategorie an/aus (mehrere Lärmlayer parallel).
    // Mehrere Kategorien teilen sich EINEN Layer (Geschäft/Party/… → laermkarte_pois): markiert
    // werden daher ALLE Zeilen aktuell sichtbarer Layer (p-highlight).
    const rowTitle = (r) => (r.layerName ? t("general.showInMap", {name: r.mainSource || r.kategorie}) : undefined);
    const rowHighlighted = (r) => r.layerName && visibleLayers.has(r.layerName);
    return (
        <DataTable
            value={rows}
            dataKey="rowKey"
            cellClassName="noise-location-table-outer-cell"
            rowClassName={(r) => `cursor-pointer${rowHighlighted(r) ? " p-highlight" : ""}`}
            onRowClick={(e) => e.data?.layerName && onToggleLayer(e.data.layerName)}
        >
            <Column
                header={<h4 className="m-0">{t("noise.col_main_category")}</h4>}
                field="mainSource"
                sortable={true}
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        {r.mainSource}
                    </div>
                )}
            />
            <Column
                header={<h4 className="m-0">{t("noise.col_sub_category")}</h4>}
                field="kategorie"
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        {r.kategorie}
                    </div>
                )}
            />
            <Column
                header={<h4 className="m-0">{t("noise.col_level")}</h4>}
                field="dB"
                sortable={true}
                align={"center"}
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        <Tag
                            value={r.dB}
                            style={r.color ? {backgroundColor: r.color, color: "rgb(75, 85, 99)"} : FALLBACK_TAG_STYLE}
                        />
                    </div>
                )}
            />
        </DataTable>
    );
}

/**
 * Emission map. Renders every noise layer and every (non-noise) emission-type layer up-front and
 * toggles their visibility via the two visible-sets, so switching stays inside one map instance
 * (updateLayer togglet visible in-place → Tile-Cache bleibt). Lärm- und Emissionslayer schließen
 * sich fachlich aus (nur eine Familie ist je gleichzeitig gesetzt). Der Tooltip zeigt Lärm-Infos,
 * solange Lärmlayer aktiv sind, sonst die Emissions-Infos der sichtbaren Typen.
 * Endpunkte ohne Recht fragt der Tooltip nicht ab: ein 403 landete sonst als Fehler im Store und
 * ersetzte den ganzen Tab durch die Fehlerseite (#19552).
 * @param {{visibleNoiseLayers:Set<string>, visibleEmissionIds:Set<number>, recenter:boolean, hasNoise:boolean, hasEmission:boolean}} props
 */
function EmissionMap({visibleNoiseLayers, visibleEmissionIds, recenter, hasNoise, hasEmission}) {
    const {t, language} = useRevaTranslation();
    const addressData = useSelector(selectAddressData);
    const dispatch = useDispatch();
    const {initialView, onMapMoveEnd} = useMapSettings("noise_map_settings");
    const showNoise = visibleNoiseLayers.size > 0;

    // Tooltip zeigt IMMER ALLE Belastungen am Punkt (Lärmquellen + Emissionsquellen) als eine
    // gemeinsame Liste — analog zu Klimarisiken, unabhängig davon, welche Layer gerade aktiv sind.
    // Dafür beide LocationInfo-Endpunkte parallel abfragen und zusammenführen.
    const fetchData = (coordinate) =>
        Promise.all([
            hasNoise ? dispatch(queryLocationInfoAtCoordinateAsync(coordinate, LocationInfoEndpoint.NOISE)) : null,
            hasEmission
                ? dispatch(queryLocationInfoAtCoordinateAsync(coordinate, LocationInfoEndpoint.EMISSION))
                : null,
        ]).then(([noise, emission]) => ({noise, emission}));

    /** @type {(data:{noise:*, emission:*})=>string} Gemeinsame Liste aller Belastungen am Punkt. */
    function buildTooltipText(data) {
        const rows = [];
        (data?.noise?.noise ?? [])
            .filter((n) => n.dB !== NOISE_DB_NO_IMPACT)
            .sort((a, b) => (b.dbLow ?? -1) - (a.dbLow ?? -1))
            .forEach((n) => {
                rows.push({name: noiseSubCategoryLabel(n.kategorie, t), value: noiseLevelLabel(n.dB, t)});
            });
        (data?.emission?.emission ?? [])
            .filter((e) => e.hasImpact)
            .sort((a, b) => (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity))
            .forEach((e) => {
                const type = EMISSION_TYPES.find((et) => et.kategorie === e.kategorie);
                const name = e.name || (type ? t(`emission.types.${type.key}`) : e.kategorie);
                const value =
                    e.distanceM != null ? t("emission.distance", {distance: e.distanceM}) : t("emission.impact_yes");
                rows.push({name, value});
            });
        if (rows.length === 0) return t("noise.no_impact");
        return rows
            .map((r) => `<div class="flex"><span class="w-40 pr-2">${r.name}</span><span>${r.value}</span></div>`)
            .join("");
    }

    /** @type {SelectCapabilitiesData|null} */
    const capabilities = useSelector(selectCapabilities);
    const backgroundLayerCapabilities = capabilities?.[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND];

    if (!backgroundLayerCapabilities || !addressData) return null;

    const noiseLayers = NOISE_LAYERS.map((l) =>
        LayerFactory.createGeoserverWmsLayer(l.name, addressData, undefined, l.key, undefined, {
            title: l.title,
            // Overlay (nicht "base"): mehrere Lärmlayer dürfen gleichzeitig sichtbar sein.
            visible: visibleNoiseLayers.has(l.name),
            // Bei Überlappung mehrerer Lärmquellen additiv-artig abdunkeln statt nur überdecken.
            blendMode: "multiply",
        }),
    );
    const emissionLayers = SELECTABLE_EMISSION_TYPES.map((et) =>
        LayerFactory.createGeoserverWmsLayer(
            EMISSION_LAYER_NAME,
            addressData,
            undefined,
            `emission_${et.id}`,
            undefined,
            {
                title: t(`emission.types.${et.key}`),
                visible: visibleEmissionIds.has(et.id),
                params: {viewparams: `id:${et.id}`},
                // Wie bei Lärm: Überlappungen mehrerer Emissionsquellen additiv-artig abdunkeln.
                blendMode: "multiply",
            },
        ),
    );

    /**
     * Map Model from address.
     * @type {MapModel}
     */
    const mapModel = {
        view: {
            lat: recenter ? addressData.lat : (initialView?.lat ?? addressData.lat),
            lon: recenter ? addressData.lon : (initialView?.lon ?? addressData.lon),
            // Start auf rundem Maßstab 1:5.000, sofern keine gespeicherte Ansicht existiert (bzw. beim Recentern)
            ...(recenter || initialView?.zoom == null
                ? {resolution: resolutionFromScale(5000, addressData.lat)}
                : {zoom: initialView.zoom}),
            minZoom: 11,
        },
        layers: [
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
                addressData,
                undefined,
                "location_micro_background",
                undefined,
                {
                    ...backgroundLayerCapabilities,
                },
            ),
            ...noiseLayers,
            ...emissionLayers,
            LayerFactory.createMarkerLayer(addressData.address, language),
            LayerFactory.createMarkerLayer([], language, "tooltip-markers"),
        ],
        eventHandlers: {
            click_event: (map, evt) => tooltipHandler(map, evt, fetchData, buildTooltipText),
            hover_feature_event: (map, evt) => tooltipHandler(map, evt, fetchData, buildTooltipText),
            moveend_event: onMapMoveEnd,
        },
        scaleLine: {enabled: true},
    };

    // KEIN React-key → nie ein Remount: Layer werden über updateLayers in-place ein-/ausgeblendet
    // (setVisible), auch beim Wechsel Lärm↔Emission → kein Flackern, kein Hintergrund-Reload.
    // Die Legende ist IMMER als Element vorhanden (kein DOM-Zweigwechsel in OpenLayersMap → kein
    // removeChild-Crash); im Emissionsmodus wird nur ihr Button/Overlay über buttonVisible versteckt.
    return (
        <div className="OpenLayersMapBackdrop w-full h-full">
            <OpenLayersMap
                model={mapModel}
                legend={<NoiseLegend />}
                legendOptions={{
                    visible: showNoise,
                    buttonVisible: showNoise,
                    // Nicht bei Klick außerhalb schließen (z.B. Klick auf eine Tabellenzeile),
                    // sonst verschwände die Legende beim Umschalten innerhalb der Lärmfamilie.
                    dismissable: false,
                    buttonLabel: t("noise.noise_classes_label"),
                }}
            />
        </div>
    );
}

/**
 * Formats a distance for the emission table: rounded to the nearest 100 m, and switched to km
 * (one decimal place) from 1000 m upward, e.g. "in rd. 100 m" / "in rd. 1,1 km".
 */
function formatApproxDistance(distanceM, t, language) {
    if (distanceM == null) return "";
    const rounded = Math.round(distanceM / 100) * 100;
    if (rounded < 1000) {
        return t("emission.distance_m", {distance: rounded});
    }
    const km = (rounded / 1000).toLocaleString(language, {minimumFractionDigits: 1, maximumFractionDigits: 1});
    return t("emission.distance_km", {distance: km});
}

/**
 * Table of every emission entry returned by the emission LocationInfo response (one row per
 * source category). Clicking a row toggles that emission type on the map (mehrere gleichzeitig,
 * additiv untereinander); highlighted rows = currently shown emission types.
 * @param {{emission:Array, visibleEmissionIds:Set<number>, onToggleEmissionType:(t:EmissionType)=>void}} props
 */
function EmissionDetails({emission, visibleEmissionIds, onToggleEmissionType}) {
    const {t, language} = useRevaTranslation();
    // Default nach Belastung hoch→niedrig (betroffene Quellen zuerst), als Zweites nach Distanz
    // aufsteigend (nahe zuerst); fehlende Distanz ans Ende.
    const rows = emission
        .map((e) => {
            const emissionType = EMISSION_TYPES.find((et) => et.kategorie === e.kategorie);
            return {
                emissionType,
                source: emissionType ? t(`emission.types.${emissionType.key}`) : e.kategorie,
                impact: e.hasImpact ? t("emission.impact_yes") : t("emission.impact_no"),
                hasImpact: e.hasImpact,
                distanceM: e.distanceM,
                distance: formatApproxDistance(e.distanceM, t, language),
                kategorie: e.kategorie,
            };
        })
        .sort(
            (a, b) =>
                (b.hasImpact === true) - (a.hasImpact === true) ||
                (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity),
        );
    // Tooltip wie bei Klimarisiken: nur auf klickbaren Zeilen (mit Emissionstyp).
    const rowTitle = (r) => (r.emissionType ? t("general.showInMap", {name: r.source}) : undefined);
    const rowHighlighted = (r) => r.emissionType && visibleEmissionIds.has(r.emissionType.id);
    return (
        <DataTable
            value={rows}
            dataKey="kategorie"
            cellClassName="noise-location-table-outer-cell"
            rowClassName={(r) => `cursor-pointer${rowHighlighted(r) ? " p-highlight" : ""}`}
            onRowClick={(e) => e.data?.emissionType && onToggleEmissionType(e.data.emissionType)}
        >
            <Column
                header={<h4 className="m-0">{t("emission.col_source")}</h4>}
                field="source"
                sortable={true}
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        {r.source}
                    </div>
                )}
            />
            <Column
                header={<h4 className="m-0">{t("emission.col_distance")}</h4>}
                field="distance"
                sortable={true}
                align={"center"}
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        {r.distance}
                    </div>
                )}
            />
            <Column
                header={<h4 className="m-0">{t("emission.col_level")}</h4>}
                field="impact"
                sortable={true}
                align={"center"}
                body={(r) => (
                    <div className="noise-location-table-inner-cell" title={rowTitle(r)}>
                        <Tag value={r.impact} style={r.hasImpact ? {backgroundColor: "#FF9854"} : FALLBACK_TAG_STYLE} />
                    </div>
                )}
            />
        </DataTable>
    );
}

/**
 * Left-hand emission-source overview (always visible below the noise table).
 * @param {{visibleEmissionIds:Set<number>, onToggleEmissionType:(t:EmissionType)=>void}} props
 */
function EmissionOverview({visibleEmissionIds, onToggleEmissionType}) {
    const {t} = useTranslation();
    const loading = useSelector(selectEmissionLoading);
    const queryError = useSelector(selectEmissionError);
    /** @type {{emission:Array}|null} */
    const emissionData = useSelector(selectEmissionData);

    if (queryError) {
        return <ErrorIndicator errorObject={queryError} titleI18nKey="emission.error" />;
    }
    if (loading || !emissionData) {
        return <LoadingIndicator loading={true} />;
    }

    return (
        <div>
            <h3 className="element-title table-title mt-3 mb-3 ml-2">{t("emission.types_label")}</h3>
            <div className={"element-panel"}>
                <EmissionDetails
                    emission={emissionData.emission ?? []}
                    visibleEmissionIds={visibleEmissionIds}
                    onToggleEmissionType={onToggleEmissionType}
                />
            </div>
        </div>
    );
}

/** Leere, stabile Set-Referenz als Initial-State des Sichtbarkeits-Sets. */
const EMPTY_LAYER_SET = new Set();

/**
 *
 * @returns {React.JSX.Element}
 */
export function TabEmission() {
    const loading = useSelector(selectNoiseLoading);
    const queryError = useSelector(selectNoiseError);
    const {t} = useTranslation();
    /** @type {LocationInfoNoise|null} */
    const noiseData = useSelector(selectNoiseData);
    /** @type {{emission:Array}|null} */
    const emissionData = useSelector(selectEmissionData);
    // #19552: Lärm und Emissionsquellen haben je ein eigenes Recht; der Tab zeigt nur die erlaubten Blöcke.
    const hasNoise = useCheckPermission(PERMISSION_RULE_LOCATION_NOISE);
    const hasEmission = useCheckPermission(PERMISSION_RULE_LOCATION_EMISSION);

    // Sichtbare Layer als EIN Token-Set (Lärm-Layername oder "emission:<id>"). Lärm und Emission
    // schließen sich aus: additiv nur innerhalb der Familie. Default wird per Effekt gesetzt.
    const [visible, setVisible] = useState(EMPTY_LAYER_SET);
    const initializedRef = useRef(false);
    const touchedRef = useRef(false);

    // Default-Karte: Lärmbelastung vorhanden → lauteste Hauptkategorie; sonst größte (nächste)
    // Emission; sonst kein Layer. Läuft einmalig, sobald die nötigen Daten geladen sind.
    useEffect(() => {
        if (touchedRef.current || initializedRef.current) return;
        if (hasNoise) {
            if (!noiseData?.noise) return;
            const topNoise = noiseData.noise
                .filter((n) => n.dbLow != null && noiseSourceOf(n.kategorie)?.layer)
                .toSorted((a, b) => b.dbLow - a.dbLow)[0];
            if (topNoise) {
                initializedRef.current = true;
                setVisible(new Set([noiseSourceOf(topNoise.kategorie).layer]));
                return;
            }
        }
        if (!hasEmission) {
            initializedRef.current = true;
            return;
        }
        // Keine Lärmbelastung → auf Emissionsdaten warten, dann größte (nächste betroffene) Emission.
        if (!emissionData) return;
        initializedRef.current = true;
        const topEmission = (emissionData.emission ?? [])
            .filter((e) => e.hasImpact && SELECTABLE_EMISSION_TYPES.some((et) => et.kategorie === e.kategorie))
            .toSorted((a, b) => (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity))[0];
        if (topEmission) {
            const type = SELECTABLE_EMISSION_TYPES.find((et) => et.kategorie === topEmission.kategorie);
            setVisible(new Set([emissionToken(type.id)]));
        }
        // sonst: leer lassen (kein Layer)
    }, [noiseData, emissionData, hasNoise, hasEmission]);

    // Memoisiert, damit die abgeleiteten Sets über Renders stabil bleiben (weniger Prop-Churn
    // an EmissionMap/EmissionDetails); nur bei geändertem `visible` neu berechnen.
    const visibleNoiseLayers = useMemo(() => new Set([...visible].filter((t) => !isEmissionToken(t))), [visible]);
    const visibleEmissionIds = useMemo(
        () => new Set([...visible].filter(isEmissionToken).map(emissionIdFromToken)),
        [visible],
    );
    const anyNoise = visibleNoiseLayers.size > 0;
    const anyEmission = visibleEmissionIds.size > 0;

    // Toggle innerhalb einer Familie additiv; ein Klick in die ANDERE Familie ersetzt die Auswahl
    // (Lärm- und Emissionslayer nie gemeinsam).
    const toggleNoiseLayer = (layerName) => {
        if (!layerName) return;
        touchedRef.current = true;
        setVisible((prev) => {
            const next = [...prev].some(isEmissionToken) ? new Set() : new Set(prev);
            next.has(layerName) ? next.delete(layerName) : next.add(layerName);
            return next;
        });
    };
    const toggleEmissionType = (emissionType) => {
        if (!emissionType) return;
        const tok = emissionToken(emissionType.id);
        touchedRef.current = true;
        setVisible((prev) => {
            const next = [...prev].some((t) => !isEmissionToken(t)) ? new Set() : new Set(prev);
            next.has(tok) ? next.delete(tok) : next.add(tok);
            return next;
        });
    };

    if (hasNoise && queryError) {
        return <ErrorIndicator errorObject={queryError} titleI18nKey="noise.error" />;
    }

    if (hasNoise && (loading || !noiseData)) return <LoadingIndicator loading={true} />;

    return (
        <div className="flex-1">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="location" tab="noise" />
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_EMISSION_TAB}>
                <div className="MapSplit h-full">
                    {/* Linke Spalte in Scroll-Containment kapseln (wie Klimarisiken-Tab), damit
                        die Tabellen/Erläuterungen die Flex-Row NICHT in die Höhe strecken und die
                        Karte immer gleich hoch bleibt; links bei Bedarf ein Scrollbalken. */}
                    <div className="MapSplitLeft flex flex-col">
                        <div className="flex-1 relative">
                            <div className="absolute w-full h-full overflow-auto">
                                {hasNoise && (
                                    <>
                                        <h3 className="element-title table-title mt-3 mb-3 ml-2">
                                            {t("noise.types_label")}
                                        </h3>
                                        <div className={"element-panel"}>
                                            <NoiseDetails
                                                noiseData={noiseData.noise}
                                                visibleLayers={visibleNoiseLayers}
                                                onToggleLayer={toggleNoiseLayer}
                                            />
                                        </div>
                                    </>
                                )}
                                {hasEmission && (
                                    <EmissionOverview
                                        visibleEmissionIds={visibleEmissionIds}
                                        onToggleEmissionType={toggleEmissionType}
                                    />
                                )}
                                {/* Erläuterung unter den Tabellen: passend zur aktiven Layer-Familie. */}
                                {(anyNoise || anyEmission) && (
                                    <div className="element-panel mb-0 mt-3">
                                        {anyNoise ? <NoiseNotes /> : <EmissionNotes />}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="MapSplitRight OpenLayersMapBackdrop relative">
                        <MapLayerUnavailableHint layers={[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND]} />
                        {/* Familien sind exklusiv: ist keine Lärm-/Emissionsquelle aktiv, ist das
                            jeweilige Set ohnehin leer -> direkt durchreichen (kein Ternär nötig). */}
                        <EmissionMap
                            visibleNoiseLayers={visibleNoiseLayers}
                            visibleEmissionIds={visibleEmissionIds}
                            recenter={false}
                            hasNoise={hasNoise}
                            hasEmission={hasEmission}
                        />
                    </div>
                </div>
            </MissingPermissionIndicator>
        </div>
    );
}

export default TabEmission;
